import { broker, backendCommandsFactory } from "./appImportBroker.js";
import { contactsExportType } from "./const/contactsExportType.js";
import { ExportContactsBot } from "./ExportContactsBot.js";
import { LinkedInSearchPageContactsIterator } from "./iterators/LinkedInSearchPageContactsIterator";
import { LinkedInGroupContactsIterator } from "./iterators/LinkedInGroupContactsIterator";
import { SalesNavigatorSearchPageIterator } from "./iterators/SalesNavigatorSearchPageIterator";
import { SalesNavigatorLeadsListIterator } from "./iterators/SalesNavigatorLeadsListIterator";
import { PublicIdentifierListContactsIterator } from "./iterators/PublicIdentifierListContactsIterator";
import { CsvListContactsIterator } from "./iterators/CsvListContactsIterator";
import { LinkedInMyConnectionsIterator } from "./iterators/LinkedInMyConnectionsIterator";
import { InvalidOperationError } from "./common/errors/InvalidOperationError.js";
import { HandledError } from "./common/errors/HandledError.js";
import { backendEventsFactory } from "./backendEventsFactory.js";

let _bot = null;

function getState() {
  const result = _bot ? _bot.getState() : null;
  return result;
}

async function startExportFromLinkedInSearchPage(href, metadata) {
  const uri = new URL(href);
  const iterator = new LinkedInSearchPageContactsIterator(broker, uri);
  _bot = new ExportContactsBot(
    broker,
    iterator,
    contactsExportType.linkedInContactsSearchPage
  );
  const result = await _bot.start(metadata);
  return result;
}

async function startExportFromLinkedInGroupPage(
  href,
  typeaheadQuery,
  metadata
) {
  const uri = new URL(href);
  const iterator = new LinkedInGroupContactsIterator(
    broker,
    uri,
    typeaheadQuery
  );
  _bot = new ExportContactsBot(
    broker,
    iterator,
    contactsExportType.linkedInGroupContactsPage
  );
  const result = await _bot.start(metadata);
  return result;
}

async function startExportFromSalesNavigatorSearchPage(href, metadata) {
  const uri = new URL(href);
  const iterator = new SalesNavigatorSearchPageIterator(broker, uri);
  _bot = new ExportContactsBot(
    broker,
    iterator,
    contactsExportType.salesNavigatorContactsSearchPage
  );
  const result = await _bot.start(metadata);
  return result;
}

async function startExportFromSalesNavigatorLeadsList(href, metadata) {
  const uri = new URL(href);
  const iterator = new SalesNavigatorLeadsListIterator(broker, uri);
  _bot = new ExportContactsBot(
    broker,
    iterator,
    contactsExportType.salesNavigatorLeadsListPage
  );
  const result = await _bot.start(metadata);
  return result;
}

async function startExportFromPublicIdentifiersList(
  publicIdentifiers,
  metadata
) {
  const iterator = new PublicIdentifierListContactsIterator(
    broker,
    publicIdentifiers
  );
  _bot = new ExportContactsBot(
    broker,
    iterator,
    contactsExportType.contactsPublicIdentifierList
  );
  const result = await _bot.start(metadata);
  return result;
}

async function startExportFromCsvContactsList(contacts, metadata) {
  const iterator = new CsvListContactsIterator(broker, contacts);
  _bot = new ExportContactsBot(
    broker,
    iterator,
    contactsExportType.csvContactsList
  );
  const result = await _bot.start(metadata);
  return result;
}

async function startExportFromLinkedInMyConnections(metadata) {
  const iterator = new LinkedInMyConnectionsIterator(broker);
  _bot = new ExportContactsBot(
    broker,
    iterator,
    contactsExportType.linkedInMyConnections
  );
  const result = await _bot.start(metadata);
  return result;
}

async function start(args) {
  const parameter = args.parameter;
  if (!_bot) {
    let result = null;
    switch (parameter.type) {
      case contactsExportType.linkedInContactsSearchPage:
        result = await startExportFromLinkedInSearchPage(
          parameter.details.url,
          args.metadata
        );
        break;
      case contactsExportType.linkedInGroupContactsPage:
        result = await startExportFromLinkedInGroupPage(
          parameter.details.url,
          parameter.details.tag?.typeaheadQuery,
          args.metadata
        );
        break;
      case contactsExportType.salesNavigatorContactsSearchPage:
        result = await startExportFromSalesNavigatorSearchPage(
          parameter.details.url,
          args.metadata
        );
        break;
      case contactsExportType.salesNavigatorLeadsListPage:
        result = await startExportFromSalesNavigatorLeadsList(
          parameter.details.url,
          args.metadata
        );
        break;
      case contactsExportType.contactsPublicIdentifierList:
        result = await startExportFromPublicIdentifiersList(
          parameter.details.publicIdentifiers,
          args.metadata
        );
        break;
      case contactsExportType.csvContactsList:
        result = await startExportFromCsvContactsList(
          parameter.details.contacts,
          args.metadata
        );
        break;
      case contactsExportType.linkedInMyConnections:
        result = await startExportFromLinkedInMyConnections(args.metadata);
        break;
      default:
        throw new InvalidOperationError(
          `Not supported contacts export type - ${parameter.type}.`,
          args.metadata
        );
    }
    return result;
  }
  //ToDo: localize message
  throw new HandledError(
    "You can't start several export at the same time.",
    args.metadata
  );
}

async function stop(args) {
  const result = await _bot.stop(args.metadata);
  return result;
}

async function clear(args) {
  const result = undefined;
  if (_bot) {
    const state = _bot.getState();
    _bot = null;
    const exportCleared = backendEventsFactory.createExportCleared(
      state,
      args.metadata
    );
    await broker.fireEvent(exportCleared);
  }
  return result;
}

async function getExportHref(exportId, metadata) {
  const command = backendCommandsFactory.automation.createGetExportHref(
    exportId,
    metadata
  );
  const result = await broker.publish(command);
  return result;
}

async function countProfilesInLinkedInSearch(args) {
  const uri = new URL(args.parameter.url);
  const iterator = new LinkedInSearchPageContactsIterator(broker, uri);
  const bot = new ExportContactsBot(
    broker,
    iterator,
    contactsExportType.linkedInContactsSearchPage
  );
  const result = await bot.count(args.metadata);
  return result;
}

async function countProfilesInLinkedInGroup(args) {
  const uri = new URL(args.parameter.url);
  const iterator = new LinkedInGroupContactsIterator(broker, uri, "");
  const bot = new ExportContactsBot(
    broker,
    iterator,
    contactsExportType.linkedInGroupContactsPage
  );
  const result = await bot.count(args.metadata);
  return result;
}

async function countProfilesInSalesNavigatorSearch(args) {
  const uri = new URL(args.parameter.url);
  const iterator = new SalesNavigatorSearchPageIterator(broker, uri);
  const bot = new ExportContactsBot(
    broker,
    iterator,
    contactsExportType.salesNavigatorContactsSearchPage
  );
  const result = await bot.count(args.metadata);
  return result;
}

async function countProfilesInSalesNavigatorLeadsList(args) {
  const uri = new URL(args.parameter.url);
  const iterator = new SalesNavigatorLeadsListIterator(broker, uri);
  const bot = new ExportContactsBot(
    broker,
    iterator,
    contactsExportType.salesNavigatorLeadsListPage
  );
  const result = await bot.count(args.metadata);
  return result;
}

async function countProfilesInLinkedInMyConnections(args) {
  const iterator = new LinkedInMyConnectionsIterator(broker);
  const bot = new ExportContactsBot(
    broker,
    iterator,
    contactsExportType.linkedInMyConnections
  );
  const result = await bot.count(args.metadata);
  return result;
}

export const exportContactsModule = {
  start,
  stop,
  clear,
  getState,
  getExportHref,
  countProfilesInLinkedInSearch,
  countProfilesInLinkedInGroup,
  countProfilesInSalesNavigatorSearch,
  countProfilesInSalesNavigatorLeadsList,
  countProfilesInLinkedInMyConnections,
};
